import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ClipboardCheck, AlertTriangle, XCircle, CheckCircle2, Scale } from "lucide-react";
import LogToTrackerButton from "@/components/protocol/LogToTrackerButton";

const INDICATIONS = [
  "Patient 35 years of age and older",
  "Chest pain consistent with cardiac ischemia",
  "Time of Onset < 12 hours",
  "12-Lead: >= 1.0 mm elevation in 2 adjacent precordial leads V5-V6",
  "12-Lead: >= 2.0 mm elevation in V1-V4",
  "12-Lead: >= 1.0 mm elevation in 2 contiguous leads",
  "12-Lead: LBBB (new or old)",
];

const ABSOLUTE_CONTRAINDICATIONS = [
  "History of ANY intracranial hemorrhage",
  "History of ischemic stroke within preceding 3 months",
  "Presence of cerebral vascular malformation",
  "Known primary or metastatic intracranial malignancy",
  "Signs suggestive of aortic dissection",
  "Bleeding diathesis or active bleeding (except menses)",
  "Significant closed head/facial trauma within preceding 3 months",
  "Intracranial or intraspinal surgery within 2 months",
  "Uncontrolled hypertension unresponsive to treatment",
];

const RELATIVE_CONTRAINDICATIONS = [
  "History of chronic, severe, poorly controlled hypertension",
  "Uncontrolled HTN at presentation (SBP >180 and/or DBP >110)",
  "History of ischemic stroke > 6 months previously",
  "Dementia",
  "Traumatic or prolonged (> 10 min) CPR",
  "Known intracranial disease",
  "Major surgery within preceding 3 weeks",
  "Recent (2-4 weeks) internal bleeding",
  "Active peptic ulcer",
  "Non-compressible vascular punctures",
  "Pregnancy",
  "Current use of anticoagulants (not ASA)",
];

const WEIGHT_DOSES = [
  { range: "< 60 kg", dose: "30 mg", volume: "6 mL" },
  { range: "60-69 kg", dose: "35 mg", volume: "7 mL" },
  { range: "70-79 kg", dose: "40 mg", volume: "8 mL" },
  { range: "80-89 kg", dose: "45 mg", volume: "9 mL" },
  { range: "> 90 kg", dose: "50 mg", volume: "10 mL" },
];

function CheckItem({ label, checked, onChange, variant = "default" }) {
  const colors = {
    default: "border-border",
    danger: "border-red-500/40",
    warning: "border-amber-500/40",
  };
  return (
    <label className={`flex items-start gap-2.5 p-2 rounded-lg border ${colors[variant]} cursor-pointer hover:bg-secondary/30 transition-colors`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 w-4 h-4 shrink-0 accent-primary"
      />
      <span className="text-sm leading-snug">{label}</span>
    </label>
  );
}

export default function ThrombolyticChecklist() {
  const [open, setOpen] = useState(false);
  const [indications, setIndications] = useState(new Set());
  const [absolutes, setAbsolutes] = useState(new Set());
  const [relatives, setRelatives] = useState(new Set());

  const toggle = (set, setter, idx) => {
    const next = new Set(set);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    setter(next);
  };

  const hasAbsolute = absolutes.size > 0;
  const hasRelative = relatives.size > 0;
  const indicationsMet = indications.size >= 3; // age + chest pain + time + at least one ECG
  const canProceed = indicationsMet && !hasAbsolute && !hasRelative;

  const handleReset = () => {
    setIndications(new Set());
    setAbsolutes(new Set());
    setRelatives(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="tnk-checklist-trigger">
          <ClipboardCheck className="w-3.5 h-3.5" />
          TNK Checklist
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] p-0" aria-describedby={undefined}>
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" /> Tenecteplase (TNK) Checklist
          </DialogTitle>
          <p className="text-xs text-muted-foreground">CCP Only — ACP-F can thrombolyze at Health Center with WGH ER MD consult</p>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-80px)] px-4 pb-4">
          <div className="space-y-4">
            {/* Status Banner */}
            <div className={`rounded-lg border-2 p-3 text-center text-sm font-semibold ${
              hasAbsolute ? 'border-red-500 bg-red-500/10 text-red-500' :
              hasRelative ? 'border-amber-500 bg-amber-500/10 text-amber-500' :
              canProceed ? 'border-green-500 bg-green-500/10 text-green-500' :
              'border-border bg-muted/30 text-muted-foreground'
            }`} data-testid="tnk-status">
              {hasAbsolute && <><XCircle className="w-4 h-4 inline mr-1.5" />DO NOT ADMINISTER — Absolute Contraindication Present</>}
              {!hasAbsolute && hasRelative && <><AlertTriangle className="w-4 h-4 inline mr-1.5" />CONSULT Flight Physician — Relative Contraindication</>}
              {canProceed && <><CheckCircle2 className="w-4 h-4 inline mr-1.5" />Checklist Clear — May Proceed</>}
              {!hasAbsolute && !hasRelative && !canProceed && "Complete checklist below"}
            </div>

            {/* Indications */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Indications
                <Badge variant="outline" className="text-[10px] ml-auto">{indications.size}/{INDICATIONS.length}</Badge>
              </h3>
              <div className="space-y-1.5">
                {INDICATIONS.map((item, i) => (
                  <CheckItem key={i} label={item} checked={indications.has(i)} onChange={() => toggle(indications, setIndications, i)} />
                ))}
              </div>
            </div>

            {/* Absolute Contraindications */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-red-500">
                <XCircle className="w-4 h-4" />
                Absolute Contraindications
                {hasAbsolute && <Badge className="bg-red-500 text-white text-[10px] ml-auto">{absolutes.size} PRESENT</Badge>}
              </h3>
              <p className="text-xs text-red-400 mb-2">If ANY present — Do NOT administer TNK</p>
              <div className="space-y-1.5">
                {ABSOLUTE_CONTRAINDICATIONS.map((item, i) => (
                  <CheckItem key={i} label={item} checked={absolutes.has(i)} onChange={() => toggle(absolutes, setAbsolutes, i)} variant="danger" />
                ))}
              </div>
            </div>

            {/* Relative Contraindications */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-amber-500">
                <AlertTriangle className="w-4 h-4" />
                Relative Contraindications
                {hasRelative && <Badge className="bg-amber-500 text-white text-[10px] ml-auto">{relatives.size} PRESENT</Badge>}
              </h3>
              <p className="text-xs text-amber-400 mb-2">If present — Consult with Flight Physician</p>
              <div className="space-y-1.5">
                {RELATIVE_CONTRAINDICATIONS.map((item, i) => (
                  <CheckItem key={i} label={item} checked={relatives.has(i)} onChange={() => toggle(relatives, setRelatives, i)} variant="warning" />
                ))}
              </div>
            </div>

            {/* Weight-Based Dosing */}
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <Scale className="w-4 h-4" />
                Tenecteplase Weight-Based Dosing
              </h3>
              <p className="text-xs text-muted-foreground mb-2">Give IV STAT over 5 seconds</p>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-secondary/50">
                      <th className="text-left p-2 font-medium">Weight</th>
                      <th className="text-center p-2 font-medium">Dose</th>
                      <th className="text-center p-2 font-medium">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {WEIGHT_DOSES.map((row, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-mono">{row.range}</td>
                        <td className="p-2 text-center font-mono font-bold">{row.dose}</td>
                        <td className="p-2 text-center font-mono text-muted-foreground">{row.volume}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Post-Thrombolytic Care */}
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3">
              <h3 className="text-sm font-semibold mb-2 text-blue-400">Post-Thrombolytic Care</h3>
              <ul className="text-xs space-y-1 text-muted-foreground">
                <li>Heparin q 10 min x 2</li>
                <li>Metoprolol 5mg IVP q 5 min</li>
                <li>Nitroglycerin 10 mcg/min INF titrate to effect and BP</li>
                <li>Lorazepam 1-2 mg SL q 5-10 hours PRN</li>
                <li>Morphine 2-4 mg IVP q 5-15 min PRN</li>
                <li>Fentanyl 25-50 mcg IV/IN q 5-15 min PRN</li>
                <li className="font-semibold text-blue-400">Follow up 12 Lead one hour post Tenecteplase</li>
              </ul>
            </div>

            {/* Reset + Log */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} className="flex-1" data-testid="tnk-reset">
                Reset Checklist
              </Button>
              <LogToTrackerButton
                className="flex-1"
                variant="default"
                testId="tnk-log"
                disabled={indications.size === 0 && absolutes.size === 0 && relatives.size === 0}
                entry={{
                  type: "intervention",
                  name: `TNK Checklist — ${
                    hasAbsolute ? "DO NOT ADMINISTER (absolute contraindication)"
                      : hasRelative ? "Consult Flight Physician (relative contraindication)"
                      : canProceed ? "Cleared to proceed"
                      : "Incomplete"
                  }`,
                  ...(canProceed ? { dose: "Cleared" } : hasAbsolute ? { dose: "Do NOT administer" } : hasRelative ? { dose: "Consult MD" } : {}),
                  notes: [
                    `Indications met: ${indications.size}/${INDICATIONS.length}`,
                    `Absolute contraindications: ${absolutes.size > 0 ? Array.from(absolutes).map((i) => ABSOLUTE_CONTRAINDICATIONS[i]).join("; ") : "none"}`,
                    `Relative contraindications: ${relatives.size > 0 ? Array.from(relatives).map((i) => RELATIVE_CONTRAINDICATIONS[i]).join("; ") : "none"}`,
                  ].join(" · "),
                }}
                successLabel="TNK checklist logged to Intervention Tracker"
                label="Log TNK to Tracker"
              />
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
